<template>
  <nav id= "main-navigation" class="main-navigation fixed top-0 left-0 right-0 z-50 nav-bar" :class="{ 'is-scrolled': isScrolled }">
    <div class="max-w-full mx-auto px-6 p-4">
      <div class="flex items-center justify-between">
        <!-- Logo -->
        <RouterLink to="/" class="flex items-center space-x-3">
          <img
            :src="darkModeMivuLogo"
            alt="MIVU Logo"
            class="h-10 w-auto hidden dark:block"
          />
          <img
            :src="lightModeMivuLogo"
            alt="MIVU Logo"
            class="h-10 w-auto dark:hidden"
          />
        </RouterLink>

        <!-- Desktop Navigation -->
        <div class="hidden lg:flex items-center space-x-2">
          <template v-for="item in sortedNavItems" :key="item.name">
            <!-- Simple Link -->
            <template v-if="!item.children">
              <RouterLink
                :to="item.to"
                class="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:glass-subtle transition-all duration-200"
                active-class="glass-subtle text-primary dark:text-blue-400 font-semibold"
              >
                {{ item.label }}
              </RouterLink>
            </template>

            <!-- Dropdown -->
            <template v-else>
              <div class="relative" @mouseenter="openDropdown(item.name)" @mouseleave="closeDropdown(item.name)">
                <button
                  class="px-4 py-2 rounded-lg text-sm font-medium text-gray-50 dark:text-gray-50 hover:glass-subtle hover:text-gray-700 dark:hover:text-gray-50 transition-all duration-200 flex items-center space-x-1"
                >
                  <span>{{ item.label }}</span>
                  <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': dropdownOpen[item.name] }" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
                
                <!-- Dropdown Menu -->
                <transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform scale-95 opacity-0"
                  enter-to-class="transform scale-100 opacity-100"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="transform scale-100 opacity-100"
                  leave-to-class="transform scale-95 opacity-0"
                >
                  <div
                    v-if="dropdownOpen[item.name]"
                    class="absolute top-full left-0 mt-2 w-56 glass-card rounded-xl shadow-2xl py-2"
                  >
                    <template v-for="child in item.children" :key="child.name">
                      <div
                        v-if="child.children"
                        class="relative"
                        @mouseenter="openSubmenu(item.name, child.name)"
                        @mouseleave="closeSubmenu(item.name, child.name)"
                      >
                        <button
                          class="bg-transparent w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:glass-subtle transition-colors mx-2 rounded-lg"
                        >
                          <span>{{ child.label }}</span>
                          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                          </svg>
                        </button>
                        <div
                          v-if="submenuOpen[`${item.name}:${child.name}`]"
                          class="absolute top-0 left-full -ml-1 w-56 glass-card rounded-xl shadow-2xl py-2"
                        >
                          <RouterLink
                            v-for="grand in child.children"
                            :key="grand.name"
                            :to="grand.to"
                            class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:glass-subtle transition-colors mx-2 rounded-lg"
                            active-class="glass-subtle text-primary dark:text-blue-400 font-medium"
                          >
                            {{ grand.label }}
                          </RouterLink>
                        </div>
                      </div>
                      <RouterLink
                        v-else
                        :to="child.to"
                        class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:glass-subtle transition-colors mx-2 rounded-lg"
                        active-class="glass-subtle text-primary dark:text-blue-400 font-medium"
                      >
                        {{ child.label }}
                      </RouterLink>
                    </template>
                  </div>
                </transition>
              </div>
            </template>
          </template>
        </div>

        <!-- Right Side Actions -->
        <div class="logout-section flex items-center space-x-3">
          <!-- User Menu -->
          <div class="relative" @mouseenter="userMenuOpen = true" @mouseleave="userMenuOpen = false">
            <button class="glass-subtle p-2 rounded-lg hover:glass-card transition-all duration-200">
              <svg class="w-6 h-6 text-gray-700 dark:text-gray-200" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
              </svg>
            </button>
            
            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="userMenuOpen"
                class="absolute right-0 top-full mt-2 w-48 glass-card rounded-xl shadow-2xl py-2"
              >
                <RouterLink to="/account" class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:glass-subtle transition-colors mx-2 rounded-lg mt-2">
                  Profile
                </RouterLink>
                <RouterLink 
                  v-if="useStore.hasAnyPermission(['Can add user', 'Can delete user', 'Can change user', 'Can view user']) || useStore.isSuperuser()"
                  to="/users" 
                  class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:glass-subtle transition-colors mx-2 rounded-lg"
                >
                  Users
                </RouterLink>
                <button @click="logout" class="bg-transparent text-left px-4 py-2 text-sm text-danger hover:glass-subtle transition-colors mx-2 rounded-lg">
                  Logout
                </button>
              </div>
            </transition>
          </div>

          <!-- Mobile Menu Toggle -->
          <button
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="lg:hidden glass-subtle p-2 rounded-lg hover:glass-card transition-all duration-200"
          >
            <svg class="w-6 h-6 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Menu -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform -translate-y-4 opacity-0"
        enter-to-class="transform translate-y-0 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="transform translate-y-0 opacity-100"
        leave-to-class="transform -translate-y-4 opacity-0"
      >
        <div v-if="mobileMenuOpen" class="lg:hidden mt-4 glass-card rounded-xl p-4 space-y-2">
          <template v-for="item in sortedNavItems" :key="item.name">
            <template v-if="!item.children">
              <RouterLink
                :to="item.to"
                @click="mobileMenuOpen = false"
                class="block px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:glass-subtle transition-all"
                active-class="glass-subtle text-primary dark:text-primary-light"
              >
                {{ item.label }}
              </RouterLink>
            </template>
            <template v-else>
              <div>
                <button
                  @click="toggleMobileDropdown(item.name)"
                  class="w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:glass-subtle transition-all"
                >
                  <span>{{ item.label }}</span>
                  <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': mobileDropdownOpen[item.name] }" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
                <div v-if="mobileDropdownOpen[item.name]" class="ml-4 mt-2 space-y-1">
                  <template v-for="child in item.children" :key="child.name">
                    <div v-if="child.children">
                      <button
                        @click="toggleMobileSubmenu(item.name, child.name)"
                        class="bg-transparent w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:glass-subtle transition-all"
                      >
                        <span>{{ child.label }}</span>
                        <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': mobileSubmenuOpen[`${item.name}:${child.name}`] }" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                      </button>
                      <div v-if="mobileSubmenuOpen[`${item.name}:${child.name}`]" class="ml-4 mt-1 space-y-1">
                        <RouterLink
                          v-for="grand in child.children"
                          :key="grand.name"
                          :to="grand.to"
                          @click="mobileMenuOpen = false"
                          class="block px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:glass-subtle transition-all"
                          active-class="glass-subtle text-primary dark:text-blue-400"
                        >
                          {{ grand.label }}
                        </RouterLink>
                      </div>
                    </div>
                    <RouterLink
                      v-else
                      :to="child.to"
                      @click="mobileMenuOpen = false"
                      class="block px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:glass-subtle transition-all"
                      active-class="glass-subtle text-primary dark:text-blue-400"
                    >
                      {{ child.label }}
                    </RouterLink>
                  </template>
                </div>
              </div>
            </template>
          </template>
        </div>
      </transition>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from '@/store';
import { getAllModules } from '@/services/moduleService';
import { logout as authLogout } from '@/services/authService';
import darkModeMivuLogo from '@/assets/mivu_logo_white.png';
import lightModeMivuLogo from '@/assets/mivu_logo.png'
import { GraduationCap } from 'lucide-vue-next'; // Added for the icon
import InteractiveTutorial from '@/components/InteractiveTutorial.vue'; // Added import

const preferredNavOrder = [
  'home', 'Network Monitoring', 'Application Monitoring', 'workspace', 'mivulite', 'organisations', 'division', 'modules', 'config', 'logs'
]

const router = useRouter();
const user = computed(() => useStore.user);

const mobileMenuOpen = ref(false);
const userMenuOpen = ref(false);
const dropdownOpen = ref({});
const mobileDropdownOpen = ref({});
const submenuOpen = ref({});
const mobileSubmenuOpen = ref({});

const HOVER_CLOSE_DELAY = 200;
const dropdownCloseTimers = {};
const submenuCloseTimers = {};
const modules = ref([]);
const error = ref(null);
const tutorialRef = ref(null); // Added ref for tutorial component

// Scroll-aware nav: subtle glass at the top, solid surface once scrolling
const isScrolled = ref(false);
const handleNavScroll = () => {
  isScrolled.value = window.scrollY > 8;
};

// Fetch modules function
const fetchModules = async () => {
  try {
    error.value = null;
    const response = await getAllModules();

    if (!response || response.length === 0) {
      return;
    }

    modules.value = response;
  } catch (err) {
    console.error('Failed to fetch modules:', err);
    error.value = 'Failed to load modules. Please try refreshing the page.';
  }
};

onMounted(async () => {
  window.addEventListener('scroll', handleNavScroll, { passive: true });
  handleNavScroll();
  const currentUser = useStore.getUser();
  if (currentUser) {
    await fetchModules();
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleNavScroll);
});


watch(
  () => useStore.getUser(),
  async (user) => {
    if (user) {
      const response = await fetchModules();
    }
  },
)

// Navigation items with reactive state - matching sidebar structure
const navigationItems = computed(() => {
  const baseItems = [
    {
      name: 'home',
      label: 'Home',
      to: '/',
    },
  ];

  // Filter items based on enabled modules for non-staff users
  const moduleBasedItems = [
    {
      name: 'Network Monitoring',
      label: 'Network Monitoring',
      realChildren: true,
      children: [
        {
          name: 'configuration',
          label: 'Configuration',
          children: [
    
        { name: 'sites', label: 'Sites', moduleName: 'Site', viewPerm: 'Can view site', to: '/sites' },
            { name: 'probe', label: 'Probes', moduleName: 'Probe', viewPerm: 'Can view probe', to: '/probe'},
            { name: 'tunnel', label: 'Tunnels', moduleName: 'Tunnel', viewPerm: 'Can view tunnel', to: '/tunnels' },
            { name: 'loadshedding', label: 'Loadshedding', moduleName: 'Loadsheddingarea', viewPerm: 'Can view loadshedding area', to: '/loadshedding' },
            { name: 'blackbox', label: 'Endpoint Availability', moduleName: 'Blackbox', viewPerm: 'Can view Blackbox', to: '/blackbox' },
            { name: 'host-availability', label: 'Host Availability', moduleName: 'HostAvailability', viewPerm: 'Can view Host Availability', to: '/host-availability' },
            { name: 'network-devices', label: 'Network Devices', moduleName: 'NetworkDevice', viewPerm: 'Can view Network Device', to: '/network-devices' },
            { name: 'alloy', label: 'Observability Configs', moduleName: 'Alloyconfig', viewPerm: 'Can view Alloy Config', to: '/alloy' },
        { name: 'ssh-collector', label: 'SSH Runner', moduleName: 'Sshcollector', viewPerm: 'Can view SSH Collector Config', to: '/ssh-collector' },
        { name: 'capture', label: 'Packet Captures', moduleName: 'Capture', viewPerm: 'Can view Capture Config', to: '/capture' },
        { name: 'fortigate', label: 'Fortigate', moduleName: 'Fortigate', viewPerm: 'Can view fortigate', to: '/fortigate' },

          ],
        },
      ],
    },
    {
      name: 'Application Monitoring',
      label: 'Application Monitoring',
      realChildren: true,
      children: [
        // One item: the APM single pane of glass. Applications / Configure /
        // Network live as tabs on that page, not as nav entries.
        { name: 'apm', label: 'Application Performance Monitoring (APM)', moduleName: 'Apm', viewPerm: 'Can view APM', to: '/apm' },
      ],
    },
    {
      name: 'division',
      label: 'Division',
      to: '/division',
      moduleName: 'Division',
      viewPerm: 'Can view division',
    },
    {
      name: 'alert-contacts',
      label: 'Alert Contacts',
      to: '/alert-contacts',
      moduleName: 'AlertContact',
      viewPerm: 'Can view alert contact',
    },
    {
      name: 'workspace',
      label: 'Workspace',
      moduleName: 'Workspace',
      to: '/agents',
      realChildren: false,
      children: [
        { name: "AgentGroups", label: "Agent Groups", to: "/agentgroup", visible: !useStore.isStaff() },
        { name: "Agents", label: "Agents", to: "/agents", visible: !useStore.isStaff() },
        { name: "RateLimits", label: "Rate Limit Configurations", to: "/ratelimits", visible: useStore.isStaff() },
        { name: "Binary", label: "Binaries", to: "/binary", visible: useStore.isStaff() },
        { name: "Config", label: "Configs", to: "/configworkspace", visible: !useStore.isStaff() },
        { name: "UpdateQueue", label: "Update Queues", to: "/updatequeue", visible: !useStore.isStaff() }
      ],
    },
    {
      name: 'mivulite',
      label: 'Lite',
      moduleName: 'Mivulite',
      to: '/device',
      children: [
        { name: "Devices", label: "Devices", to: "/device", visible: !useStore.isStaff() },
        { name: "Admin", label: "Admin", to: "/admin", visible: useStore.isSuperuser() },
      ],
    },
    {
      name: 'organisations',
      label: 'Organisations',
      to: '/organizations',
      viewPerm: 'Can view organisation',
    },
  ];

  // Add items based on user role and module status
  const items = [...baseItems];
  const currentUser = useStore.getUser();

  // Module + permission gate: a nav entry only renders if its corresponding
  // org-level Module is enabled AND the user holds the matching view perm.
  // Without the perm gate the page link is reachable but its API calls 403,
  // which is bad UX. Staff users skip the perm check (they implicitly can see
  // everything in their assigned scope).
  const isStaff = useStore.isStaff();
  const moduleEnabledFor = (moduleName) =>
    modules.value.some((m) =>
      m.name === moduleName &&
      m.enabled &&
      m.uiconfig === currentUser?.org
    );
  const hasViewPerm = (viewPerm) =>
    !viewPerm || isStaff || useStore.hasPermission(viewPerm);

  if (!isStaff) {
    moduleBasedItems.forEach((item) => {
      if (item.children && item.realChildren) {

        const isEnabled = (moduleName) =>
          modules.value.some((m) =>
            m.name === moduleName &&
            m.enabled &&
            m.uiconfig === currentUser?.org
          );

        const filteredChildren = item.children
          .map((child) => {
            if (child.children) {
              const filteredGrand = child.children.filter((g) => isEnabled(g.moduleName));
              return filteredGrand.length > 0 ? { ...child, children: filteredGrand } : null;
            }
            return isEnabled(child.moduleName) ? child : null;
          })
          .filter(Boolean);

        if (filteredChildren.length > 0) {
          items.push({ ...item, children: filteredChildren });
        }
      } else {
        if ((!item.moduleName || moduleEnabledFor(item.moduleName)) && hasViewPerm(item.viewPerm)) {
          const entry = item.children && !item.realChildren
            ? { ...item, children: item.children.filter(val => val.visible === true) }
            : item;
          items.push(entry);
        }
      }
    });

  } else {
    items.push(...moduleBasedItems);
  }

  // Add modules link if user is staff
  if (useStore.isStaff()) {
    items.push({
      name: 'modules',
      label: 'Modules',
      to: '/modules',
    });
  }

  if (useStore.hasAnyPermission(['Can commit changes to production', 'Can approve workspace agents']) || useStore.isSuperuser()) {
    items.push({
      name: 'config',
      label: 'Approval Changes',
      to: '/config',
    });
  }

  if(useStore.isSuperuser()){ 
    items.push({
      name: 'logs',
      label: 'Logs',
      children: [
        { name: 'all-logs', label: 'DRF Logs', to: '/logs' },
        { name: 'audit-logs', label: 'Audit Logs', to: '/audit-logs' },
        { name: 'access-logs', label: 'Access Logs', to: '/access-logs' },
      ],
    });  
  }

  return items;
});

const sortedNavItems = computed(() => {
  const order = new Map(preferredNavOrder.map((name, i) => [name, i]))
  return [...navigationItems.value].sort((x, y) => {
    const xi = order.get(x.name) ?? Infinity
    const yi = order.get(y.name) ?? Infinity
    return xi - yi
  })
});

const openDropdown = (name) => {
  if (dropdownCloseTimers[name]) {
    clearTimeout(dropdownCloseTimers[name]);
    dropdownCloseTimers[name] = null;
  }
  dropdownOpen.value[name] = true;
};

const closeDropdown = (name) => {
  if (dropdownCloseTimers[name]) clearTimeout(dropdownCloseTimers[name]);
  dropdownCloseTimers[name] = setTimeout(() => {
    dropdownOpen.value[name] = false;
    dropdownCloseTimers[name] = null;
  }, HOVER_CLOSE_DELAY);
};

const toggleMobileDropdown = (name) => {
  mobileDropdownOpen.value[name] = !mobileDropdownOpen.value[name];
};

const openSubmenu = (parent, child) => {
  const key = `${parent}:${child}`;
  if (submenuCloseTimers[key]) {
    clearTimeout(submenuCloseTimers[key]);
    submenuCloseTimers[key] = null;
  }
  submenuOpen.value[key] = true;
};

const closeSubmenu = (parent, child) => {
  const key = `${parent}:${child}`;
  if (submenuCloseTimers[key]) clearTimeout(submenuCloseTimers[key]);
  submenuCloseTimers[key] = setTimeout(() => {
    submenuOpen.value[key] = false;
    submenuCloseTimers[key] = null;
  }, HOVER_CLOSE_DELAY);
};

const toggleMobileSubmenu = (parent, child) => {
  const key = `${parent}:${child}`;
  mobileSubmenuOpen.value[key] = !mobileSubmenuOpen.value[key];
};

const logout = async () => {
  try {
    await authLogout();
    window.location.href = '/login';
  } catch (error) {
    console.error('Logout error:', error);
    window.location.href = '/login';
  }
};

const openTutorial = () => {
  userMenuOpen.value = false;
  mobileMenuOpen.value = false;
  if (tutorialRef.value) {
    tutorialRef.value.open();
  }
};
</script>

<style scoped>
/* Subtle glass at the top of the page, settling into a near-solid surface
   once the user scrolls so content no longer bleeds through the nav. */
.nav-bar {
  @apply bg-white/70 dark:bg-surface-dark/70 backdrop-blur-md border-b border-transparent;
  box-shadow: 0 1px 0 0 rgba(255, 255, 255, 0.04);
  transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  will-change: background-color;
}

.nav-bar.is-scrolled {
  @apply bg-white/95 dark:bg-surface-dark/95 border-gray-200/70 dark:border-white/10;
  box-shadow: 0 6px 24px -8px rgba(31, 38, 135, 0.25);
}
</style>
